import { Mutation, useMutation } from "react-query";
import {
  updateOpenHouseById,
  deleteOpenHouseById,
  addOpenHouse,
  addAttendeeToOpenHouse,
} from "../services/mock";
import queryClient from "../services/reactQuery";
import { OpenHouse } from "../types";
import moment from "moment";

const sleep = (delay: number) =>
  new Promise((resolve) => setTimeout(resolve, delay));

export function OpenHouseAddMutation({
  children,
}: {
  children: (addMutation: any) => JSX.Element;
}) {
  const add = async (openHouse: OpenHouse) => {
    const data = await addOpenHouse(openHouse);
    return data;
  };

  const addMutation = useMutation(add, {
    onSuccess: (data, variables) => {
      let openHouses = queryClient.getQueryData(["openHouses"]) as {
        upcoming: OpenHouse[];
        past: OpenHouse[];
      };

      if (openHouses) {
        const date = moment(data?.date, "MM/DD/YYYY");

        const today = moment();
        const diffTime = date.diff(today, "days");

        if (diffTime >= 0) {
          openHouses.upcoming.push(data);
        } else {
          openHouses.past.push(data);
        }

        queryClient.setQueryData(["openHouses"], openHouses);
      }
    },
  });

  return children(addMutation);
}

export function OpenHouseUpdateMutation({
  children,
}: {
  children: (updateMutation: any) => JSX.Element;
}) {
  const updateOpenHouse = async ({
    id,
    updatedData,
  }: {
    id: number;
    updatedData: OpenHouse;
  }) => {
    const data = await updateOpenHouseById(id, updatedData);
    return data;
  };

  const updateMutation = useMutation(updateOpenHouse, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData(["openHouse", variables.id], (oldData: any) => {
        return { ...oldData, ...data };
      });

      let openHouses = queryClient.getQueryData([
        "openHouse",
      ]) as Array<OpenHouse>;

      if (openHouses) {
        // find the index of the open house
        const index = openHouses.findIndex((item) => item.id === variables.id);

        // if the index is found, update the open house
        if (index !== -1) {
          openHouses[index] = { ...openHouses[index], ...data };
        }

        queryClient.setQueryData(["openHouses"], openHouses);
      }
    },
  });

  return children(updateMutation);
}

export function OpenHouseDeleteMutation({
  children,
}: {
  children: (deleteMutation: any) => JSX.Element;
}) {
  const deleteOpenHouse = async ({ id }: { id: number }) => {
    await deleteOpenHouseById(id);
  };

  const deleteMutation = useMutation(deleteOpenHouse, {
    onSuccess: (data, variables) => {
      queryClient.setQueryData(["openHouse", variables.id], undefined);

      let openHouses = queryClient.getQueryData([
        "openHouse",
      ]) as Array<OpenHouse>;

      if (openHouses) {
        openHouses = openHouses.filter((item) => item.id !== variables.id);

        queryClient.setQueryData(["openHouses"], openHouses);
      }
    },
  });

  return children(deleteMutation);
}

export function OpenHouseAttendeeAddMutation({
  children,
}: {
  children: (addMutation: any) => JSX.Element;
}) {
  const add = async ({ id, attendee }: { id: number; attendee: any }) => {
    const data = await addAttendeeToOpenHouse(id, attendee);
    return data;
  };

  const addMutation = useMutation(add, {
    onSuccess: (data, variables) => {
      let openHouse = queryClient.getQueryData([
        "openHouse",
        variables.id,
      ]) as OpenHouse;

      if (openHouse) {
        openHouse.attendees.push(variables.attendee);

        queryClient.setQueryData(["openHouse", variables.id], openHouse);
      }

      let openHouses = queryClient.getQueryData([
        "openHouse",
      ]) as Array<OpenHouse>;

      if (openHouses) {
        openHouses.push(data);

        queryClient.setQueryData(["openHouses"], openHouses);
      }
    },
  });

  return children(addMutation);
}
